import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Power, KeyRound, Trash2, Plus } from "lucide-react";
import { useAuth } from "@/lib/auth";

const ALL_SPECIALIZATIONS = ["iPhone", "iPad", "MacBook", "iMac"];

export default function FixerProfile() {
  const { user, setUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [account, setAccount] = useState({ name: "", email: "", phone_number: "" });
  const [services, setServices] = useState([]);
  // draftServices = working copy of offered services
  //   [{ service_id, labor_price: string, _new?: bool }]
  const [draftServices, setDraftServices] = useState([]);
  const [originalServices, setOriginalServices] = useState([]);
  const [addPick, setAddPick] = useState("");
  const [busy, setBusy] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm: "" });

  const load = useCallback(async () => {
    const [p, s, pr] = await Promise.all([
      api.get("/fixer/profile").then((r) => r.data),
      api.get("/services").then((r) => r.data),
      api.get("/fixer/pricing").then((r) => r.data),
    ]);
    setProfile(p);
    setServices(s);
    const rows = (pr || []).map((row) => ({
      service_id: row.service_id,
      labor_price: String(row.labor_price ?? ""),
    }));
    setDraftServices(rows);
    setOriginalServices(rows.map((r) => ({ ...r })));
  }, []);

  useEffect(() => {
    load();
    if (user) setAccount({
      name: user.name || "",
      email: user.email || "",
      phone_number: user.phone_number || "",
    });
  }, [load, user]);

  const serviceById = useMemo(() => {
    const m = {};
    for (const s of services) m[s.id] = s;
    return m;
  }, [services]);

  const offeredIds = useMemo(
    () => new Set(draftServices.map((r) => r.service_id)),
    [draftServices],
  );
  const availableToAdd = services.filter((s) => !offeredIds.has(s.id));

  const updateDraftPrice = (sid, val) => {
    setDraftServices((rows) =>
      rows.map((r) => (r.service_id === sid ? { ...r, labor_price: val } : r)),
    );
  };
  const removeDraft = (sid) => {
    setDraftServices((rows) => rows.filter((r) => r.service_id !== sid));
  };
  const addDraft = () => {
    if (!addPick) return;
    setDraftServices((rows) => [
      ...rows,
      { service_id: addPick, labor_price: "599", _new: true },
    ]);
    setAddPick("");
  };

  const toggleSpec = (c) => {
    setProfile((p) => {
      const has = p.specializations?.includes(c);
      return {
        ...p,
        specializations: has
          ? p.specializations.filter((x) => x !== c)
          : [...(p.specializations || []), c],
      };
    });
  };

  const save = async () => {
    setBusy(true);
    try {
      // 1. Fixer profile fields
      await api.patch("/fixer/profile", {
        specializations: profile.specializations,
        active: profile.active,
        business_name: profile.business_name,
      });
      // 2. Account/user fields
      const accountPatch = {};
      if (account.name && account.name !== user.name) accountPatch.name = account.name;
      if (account.phone_number !== user.phone_number) accountPatch.phone_number = account.phone_number;
      if (Object.keys(accountPatch).length > 0) {
        const { data } = await api.patch("/auth/me", accountPatch);
        setUser({ ...user, ...data });
      }
      // 3. Services — diff draft against original to compute upserts + removes
      const originalMap = new Map(
        originalServices.map((r) => [r.service_id, String(r.labor_price)]),
      );
      const draftMap = new Map(
        draftServices.map((r) => [r.service_id, String(r.labor_price)]),
      );
      const removed = [...originalMap.keys()].filter((sid) => !draftMap.has(sid));
      const upserts = [...draftMap.entries()].filter(
        ([sid, val]) => originalMap.get(sid) !== val,
      );
      // Validate prices first
      for (const [sid, val] of upserts) {
        const num = parseFloat(val);
        if (Number.isNaN(num) || num < 0) {
          toast.error(`Ugyldig pris for "${serviceById[sid]?.name || sid}"`);
          setBusy(false);
          return;
        }
      }
      await Promise.all([
        ...upserts.map(([sid, val]) =>
          api.patch("/fixer/pricing", { service_id: sid, labor_price: parseFloat(val) }),
        ),
        ...removed.map((sid) => api.delete(`/fixer/pricing/${sid}`)),
      ]);
      toast.success("Endringer lagret");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (pw.new_password !== pw.confirm) {
      toast.error("Nytt passord stemmer ikke overens");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/change-password", {
        current_password: pw.current_password,
        new_password: pw.new_password,
      });
      toast.success("Passord oppdatert");
      setPw({ current_password: "", new_password: "", confirm: "" });
      setShowPw(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  if (!profile) return <div className="tk-container py-20 text-slate-500">Laster …</div>;

  return (
    <div className="tk-container py-16 max-w-2xl" data-testid="fixer-profile-page">
      <p className="tk-overline mb-3">Verkstedprofil</p>
      <h1 className="text-3xl sm:text-4xl font-semibold mb-2">{profile.display_name}</h1>
      <p className="text-sm text-slate-500 mb-10">{profile.address}, {profile.postal_code}</p>

      <div className="tk-card mb-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-md flex items-center justify-center ${profile.active ? "bg-emerald-50" : "bg-slate-100"}`}>
            <Power size={18} className={profile.active ? "text-emerald-600" : "text-slate-400"} />
          </div>
          <div>
            <p className="font-semibold">Tilgjengelig for nye bestillinger</p>
            <p className="text-xs text-slate-500">Skru av hvis du midlertidig er fullbooket.</p>
          </div>
        </div>
        <button
          onClick={() => setProfile({ ...profile, active: !profile.active })}
          className={`relative w-12 h-6 rounded-full transition-colors ${profile.active ? "bg-[#4A7C59]" : "bg-slate-300"}`}
          data-testid="availability-toggle"
        >
          <span className={`absolute top-0.5 ${profile.active ? "left-6" : "left-0.5"} transition-all w-5 h-5 rounded-full bg-white`} />
        </button>
      </div>

      <div className="tk-card mb-6 space-y-4">
        <p className="tk-overline">Kontaktopplysninger</p>
        <div>
          <label className="text-xs text-slate-500">Navn (kontaktperson)</label>
          <input
            className="tk-input mt-1"
            value={account.name}
            onChange={(e) => setAccount({ ...account, name: e.target.value })}
            data-testid="profile-name"
          />
        </div>
        <div>
          <label className="text-xs text-slate-500">E-post (innloggings-ID — kontakt Tekkdeler for endring)</label>
          <input
            className="tk-input mt-1"
            type="email"
            value={account.email}
            readOnly
            data-testid="profile-email"
          />
        </div>
        <div>
          <label className="text-xs text-slate-500">Telefon</label>
          <input
            className="tk-input mt-1"
            value={account.phone_number}
            onChange={(e) => setAccount({ ...account, phone_number: e.target.value })}
            data-testid="profile-phone"
            placeholder="+47 …"
          />
        </div>
      </div>

      <div className="tk-card mb-6">
        <p className="tk-overline mb-3">Bedriftsnavn</p>
        <input
          className="tk-input"
          value={profile.business_name || ""}
          onChange={(e) => setProfile({ ...profile, business_name: e.target.value })}
          data-testid="profile-business-name"
        />
      </div>

      <div className="tk-card mb-6">
        <p className="tk-overline mb-3">Spesialiseringer</p>
        <p className="text-xs text-slate-500 mb-3">
          Velg hvilke enhetstyper du tar imot. Du vises kun til kunder for de kategoriene du har valgt.
        </p>
        <div className="flex flex-wrap gap-2">
          {ALL_SPECIALIZATIONS.map((c) => {
            const on = profile.specializations?.includes(c);
            return (
              <button
                key={c}
                onClick={() => toggleSpec(c)}
                className={`px-4 py-2 rounded-md text-sm border ${on ? "bg-[#0B1C30] text-white border-[#0B1C30]" : "border-[#E2E8F0] bg-white text-slate-700"}`}
                data-testid={`spec-${c}`}
              >
                {c}
              </button>
            );
          })}
        </div>
      </div>

      <div className="tk-card mb-6">
        <p className="tk-overline mb-3">Tjenester og priser (NOK)</p>
        <p className="text-xs text-slate-500 mb-4">
          Dette er prisene kundene ser for arbeidet ditt. Legg til, fjern eller endre
          som du vil — endringene lagres med knappen under.
        </p>
        {draftServices.length === 0 ? (
          <p className="text-sm text-slate-500 py-2">
            Ingen tjenester registrert. Legg til under.
          </p>
        ) : (
          <div className="space-y-2" data-testid="profile-pricing-list">
            {draftServices.map((row) => {
              const s = serviceById[row.service_id];
              return (
                <div
                  key={row.service_id}
                  className="flex items-center justify-between gap-3 py-2 border-b border-[#E2E8F0]/60 last:border-0"
                  data-testid={`pricing-row-${row.service_id}`}
                >
                  <div className="flex-1">
                    <p className="text-sm font-medium">
                      {s?.name || row.service_id}
                      {row._new && (
                        <span className="ml-2 text-[10px] uppercase tracking-wider text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded">
                          ny
                        </span>
                      )}
                    </p>
                    {s?.description && (
                      <p className="text-xs text-slate-500">{s.description}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-400">kr</span>
                    <input
                      type="number"
                      min="0"
                      value={row.labor_price}
                      onChange={(e) => updateDraftPrice(row.service_id, e.target.value)}
                      className="tk-input !py-1.5 !px-2 text-sm max-w-[110px]"
                      data-testid={`pricing-${row.service_id}`}
                    />
                    <button
                      type="button"
                      onClick={() => removeDraft(row.service_id)}
                      className="text-slate-400 hover:text-red-600"
                      data-testid={`pricing-remove-${row.service_id}`}
                      aria-label={`Fjern ${s?.name || row.service_id}`}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {availableToAdd.length > 0 && (
          <div className="flex items-center gap-2 mt-4" data-testid="add-service-row">
            <select
              value={addPick}
              onChange={(e) => setAddPick(e.target.value)}
              className="tk-input !py-1.5 text-sm flex-1"
              data-testid="add-service-picker"
            >
              <option value="">Legg til ny tjeneste …</option>
              {availableToAdd.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
            <button
              type="button"
              onClick={addDraft}
              disabled={!addPick}
              className="tk-btn-secondary !py-1.5 !px-3 text-xs"
              data-testid="add-service-btn"
            >
              <Plus size={14} /> Legg til
            </button>
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-3 items-center mb-10">
        <button
          onClick={save}
          disabled={busy}
          className="tk-btn-primary"
          data-testid="profile-save-btn"
        >
          {busy ? "Lagrer …" : "Lagre endringer"}
        </button>
        <button
          onClick={() => setShowPw((v) => !v)}
          className="tk-btn-secondary"
          data-testid="profile-change-pw-btn"
        >
          <KeyRound size={14} /> Bytt passord
        </button>
      </div>

      {showPw && (
        <form onSubmit={changePassword} className="tk-card space-y-3" data-testid="change-pw-form">
          <p className="tk-overline">Bytt passord</p>
          <input
            type="password"
            placeholder="Nåværende passord"
            className="tk-input"
            value={pw.current_password}
            onChange={(e) => setPw({ ...pw, current_password: e.target.value })}
            required
            data-testid="pw-current"
          />
          <input
            type="password"
            placeholder="Nytt passord (min 8 tegn)"
            className="tk-input"
            value={pw.new_password}
            onChange={(e) => setPw({ ...pw, new_password: e.target.value })}
            minLength={8}
            required
            data-testid="pw-new"
          />
          <input
            type="password"
            placeholder="Bekreft nytt passord"
            className="tk-input"
            value={pw.confirm}
            onChange={(e) => setPw({ ...pw, confirm: e.target.value })}
            required
            data-testid="pw-confirm"
          />
          <button type="submit" disabled={busy} className="tk-btn-primary" data-testid="pw-submit">
            Oppdater passord
          </button>
        </form>
      )}
    </div>
  );
}
